import "./polyfills";
import React from "react";
import { createRoot } from "react-dom";
import { BrowserRouter } from "react-router-dom";
import { Provider } from "react-redux";
import { QueryClientProvider, QueryClient } from "@tanstack/react-query";
import { GlobalModalContextProvider } from "src/utils/GlobalModal/GlobalModalContext";
import InspectorWrapper from "./devInspector/prod";
import * as serviceWorker from "./serviceWorker";
import "./assets/base.scss";
import Main from "./Pages/Main";
import configureStore from "./config/configureStore";
import getUserConfirmationPopup from "./utils/system/DOM/getUserConfirmationPopup";
import RbsAppWrapper from "./utils/RoleBasedSecurity/RbsAppWrapper";
import ScrollToTop from "./Layout/AppMain/ScrollToTop";
import { getVersion } from "./config/env";
import GrowthbookFeatureFlagsProvider from "./utils/GrowthBookFeatureFlags/FeatureFlagsProvider";
import GlobalErrorBoundary from "./Layout/AppMain/GlobalErrorBoundary";

const VERSION_KEY = "version";
const store = configureStore();
const rootElement = document.getElementById("root");
const root = createRoot(rootElement);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false, // default: true
      retry: false, // default: 3
    },
  },
});

if (process.env.NODE_ENV !== "development") {
  // eslint-disable-next-line global-require
  const newrelic = require("./utils/newRelic/newRelicAgent/newrelic").default;

  newrelic();
}

const renderApp = (Component) => {
  const version = localStorage?.getItem(VERSION_KEY);

  if (!version || version !== getVersion()) {
    localStorage?.clear();
    sessionStorage?.clear();
    localStorage?.setItem(VERSION_KEY, getVersion());
  }

  root.render(
    <GlobalErrorBoundary>
      <InspectorWrapper
        keys={["control", "alt", "z"]}
        disableLaunchEditor={false}
        onHoverElement={(params) => {}}
        onClickElement={(params) => {}}
      >
        <Provider store={store}>
          <RbsAppWrapper>
            <GrowthbookFeatureFlagsProvider
              clientKey={process.env.REACT_APP_GROWTHBOOK_CLIENT_KEY}
              enableDevMode={
                process.env.REACT_APP_GROWTHBOOK_DEV_MODE === "true"
              }
            >
              <BrowserRouter getUserConfirmation={getUserConfirmationPopup}>
                <QueryClientProvider client={queryClient}>
                  <GlobalModalContextProvider>
                    <ScrollToTop />
                    <Component />
                  </GlobalModalContextProvider>
                </QueryClientProvider>
              </BrowserRouter>
            </GrowthbookFeatureFlagsProvider>
          </RbsAppWrapper>
        </Provider>
      </InspectorWrapper>
    </GlobalErrorBoundary>
  );
};

renderApp(Main);

if (module.hot) {
  module.hot.accept("./Pages/Main", () => {
    // eslint-disable-next-line global-require
    const NextApp = require("./Pages/Main").default;
    renderApp(NextApp);
  });
}
serviceWorker.unregister();
